/* eslint-disable camelcase */
import {
  Box, Button, Grid, Slider, Typography, styled,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useToggle from '../../../hooks/useToggle';
import DateRangePicker from '../../../components/FormComponents/DateRangePicker';
import {
  activationDateRanges,
  defaultFilterValue,
  deleteProtectedView,
  healthScoreMarks,
  marks,
  renewalDateRanges,
} from '../../../sections/AccountList/accountListConfig';
import { AutoComplete, SearchBar, SelectBox } from '../../../components/FormComponents';
import useAccountListFilters from './useAccountListFilters';
import CustomFilterDialog from './CustomFilter/CustomFilterDialog';
import useCustomFilter from './useCustomFilter';

const IOSSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.secondary.mainColor,
}));

function AccountListFilters(props) {
  const {
    classes,
    accountList,
    featureList,
    selectedFilters,
    setSelectedFilters,
    resetFilters,
    toggleAdvanceFilters,
    setToggleAdvanceFilters,
    selectedView,
    setSelectedView,
    findSelectedView,
  } = props;

  const isDefault = !selectedView || deleteProtectedView.includes(selectedView);
  const [openDialog, setOpenDialog] = useToggle();

  const {
    dropdown,
    handleFilterChange,
    isViewConfigEnabled,
    setIsViewConfigEnabled,
  } = useAccountListFilters({
    accountList, featureList, setSelectedFilters, selectedView,
  });

  const {
    handleUpdate,
    getInitialValues,
    handleSave,
    handleEdit,
    handleDelete,
  } = useCustomFilter({
    selectedFilters,
    resetFilters,
    selectedView,
    setSelectedView,
    findSelectedView,
    setIsViewConfigEnabled,
  });

  return (
    <>
      {/* Normal filters */}
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} sm={6} lg={3.5} sx={{ alignSelf: 'center' }}>
          <SearchBar
            label="Account Name"
            value={selectedFilters.searchQuery}
            data={selectedFilters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
            onClear={() => handleFilterChange('searchQuery', '')}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2.5}>
          <SelectBox
            multiple
            title="Package"
            value={selectedFilters.package}
            data={dropdown.packageListOptions}
            onChange={(e) => handleFilterChange('package', e.target.value)}
            highlighted={selectedFilters.package.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Package Version"
            value={selectedFilters.packageVersion}
            data={dropdown.packageVersionOptions}
            onChange={(e) => handleFilterChange('packageVersion', e.target.value)}
            highlighted={selectedFilters.packageVersion.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Licence Assignment"
            value={selectedFilters.licenceAssignment}
            data={dropdown.licenceAssignmentOptions}
            onChange={(e) => handleFilterChange('licenceAssignment', e.target.value)}
            highlighted={selectedFilters.licenceAssignment.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Licence Status"
            value={selectedFilters.licenceStatus}
            data={dropdown.licenceStatusOptions}
            onChange={(e) => handleFilterChange('licenceStatus', e.target.value)}
            highlighted={selectedFilters.licenceStatus.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <AutoComplete
            multiple
            label="Feature Usage"
            value={selectedFilters.featureUsageFilter}
            options={dropdown.featureUsageFilterOptions.map(({ feature_id, feature_name }) => ({
              value: feature_id,
              name: feature_name,
            }))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, newVal) => handleFilterChange('featureUsageFilter', newVal ?? [])}
            highlighted={selectedFilters.featureUsageFilter.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            title="Feature Usage Mode"
            value={selectedFilters.featureUsageMode}
            data={dropdown.featureUsageModeOptions}
            onChange={(e) => handleFilterChange('featureUsageMode', e.target.value)}
            disabled={selectedFilters.featureUsageFilter.length === 0}
            highlighted={selectedFilters.featureUsageFilter.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Organization Edition"
            value={selectedFilters.organisationEdition}
            data={dropdown.organisationEditionOptions}
            onChange={(e) => handleFilterChange('organisationEdition', e.target.value)}
            highlighted={selectedFilters.organisationEdition.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Salesforce Instance"
            value={selectedFilters.salesforceInstance}
            data={dropdown.salesforceInstanceOptions}
            onChange={(e) => handleFilterChange('salesforceInstance', e.target.value)}
            highlighted={selectedFilters.salesforceInstance.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            title="Account Visibility"
            value={selectedFilters.accountVisibility}
            data={[defaultFilterValue, ...dropdown.accountVisibilityOptions]}
            onChange={(e) => handleFilterChange('accountVisibility', e.target.value)}
            highlighted={selectedFilters.accountVisibility !== defaultFilterValue}
          />
        </Grid>
      </Grid>

      {/* Advanced filters */}
      {toggleAdvanceFilters && (
      <Grid item container xs={12} gap={2} alignItems="center">
        <Grid item xs={12} sm={6} lg={3}>
          <DateRangePicker
            label="Activation Date"
            value={selectedFilters.activationDate}
            onChange={(newRange) => handleFilterChange('activationDate', newRange)}
            relativeDateRanges={activationDateRanges}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <DateRangePicker
            label="Renewal Date"
            value={selectedFilters.renewalDate}
            onChange={(newRange) => handleFilterChange('renewalDate', newRange)}
            relativeDateRanges={renewalDateRanges}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Country"
            value={selectedFilters.country}
            data={dropdown.packageCountryOptions}
            onChange={(e) => handleFilterChange('country', e.target.value)}
            highlighted={selectedFilters.country.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2}>
          <SelectBox
            multiple
            title="Language"
            value={selectedFilters.language}
            data={dropdown.packageLanguageOptions}
            onChange={(e) => handleFilterChange('language', e.target.value)}
            highlighted={selectedFilters.language.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <SelectBox
            multiple
            title="KPI Score Trend"
            value={selectedFilters.healthScoreTrend}
            data={dropdown.healthScoreTrendOptions}
            onChange={(e) => handleFilterChange('healthScoreTrend', e.target.value)}
            highlighted={selectedFilters.healthScoreTrend.length}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ pl: '5px', mb: 1, fontSize: 14 }}>Seats</Typography>
            <Box sx={{ pl: 2 }}>
              <IOSSlider
                marks={marks}
                min={0}
                step={5}
                max={100}
                disableSwap
                value={selectedFilters.range}
                onChange={(e, newVal, activeThumb) => handleFilterChange('range', e.target.value, activeThumb)}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <Box sx={{ width: '100%', paddingLeft: '20px' }}>
            <Typography sx={{ pl: '5px', mb: 1, fontSize: 14 }}>Score</Typography>
            <Box sx={{ pl: 2 }}>
              <IOSSlider
                marks={healthScoreMarks}
                step={5}
                min={0}
                max={100}
                disableSwap
                value={selectedFilters.healthScoreRange}
                onChange={(e, newVal, activeThumb) => handleFilterChange('healthScoreRange', e.target.value, activeThumb)}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      )}

      {/* Filter actions */}
      <Grid item container className={classes.actionStyles} xs={12}>
        {isViewConfigEnabled && (
        <Grid container item xs="auto" className="tg-custom-view">
          <Button
            variant="outlined"
            size="small"
            endIcon={isDefault ? <SaveAsIcon color="primary" fontSize="small" /> : <SaveIcon color="primary" fontSize="small" />}
            onClick={isDefault ? setOpenDialog : handleUpdate}
          >
            {isDefault ? 'Save View' : 'Update View'}
          </Button>
        </Grid>
        )}

        <Grid container item xs="auto" gap={1}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<RestartAltIcon color="primary" fontSize="small" />}
            onClick={resetFilters}
          >
            Reset
          </Button>

          <Button
            variant="outlined"
            size="small"
            endIcon={toggleAdvanceFilters ? <KeyboardArrowUpIcon color="primary" fontSize="small" /> : <KeyboardArrowDownIcon color="primary" fontSize="small" />}
            onClick={setToggleAdvanceFilters}
          >
            {toggleAdvanceFilters ? 'Less' : 'More'}
          </Button>
        </Grid>
      </Grid>

      <CustomFilterDialog
        open={openDialog}
        handleClose={setOpenDialog}
        isNew={isDefault}
        isEdit={false}
        initialValues={getInitialValues()}
        handleSave={handleSave}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
    </>
  );
}

export default AccountListFilters;
